import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { gameService } from '../services/gameService';
import { useKidsStore } from '../store/kidsStore';
import type { Game } from '@shared/types';

// Mapeo de códigos de juego a rutas
const GAME_ROUTES: { [key: string]: string } = {
  'bombillas': '/juegos/bombillas',
  'luces-sombras': '/juegos/bombillas',
  'termometros': '/juegos/termometros',
  'bricks': '/juegos/ladrillos',
  'ladrillos': '/juegos/ladrillos',
  'hitori': '/juegos/hitori',
  'kakurasu': '/juegos/kakurasu',
  'binary': '/juegos/binary',
  'camping': '/juegos/camping',
  'torres': '/juegos/torres',
  'futoshiki': '/juegos/futoshiki',
  'nurikabe': '/juegos/nurikabe',
  'suguru': '/juegos/suguru',
  'mastermind': '/juegos/mastermind',
  'dueto': '/juegos/dueto',
  'convergencia': '/juegos/convergencia',
  'flechas': '/juegos/flechas',
  'vecinos': '/juegos/vecinos',
  'itv_express': '/juegos/itv-express',
  'tetravex': '/juegos/tetravex',
  'frito_variado': '/juegos/frito-variado',
  'tasukuea': '/juegos/tasukuea',
  'senderos': '/juegos/senderos',
  'sashigane': '/juegos/sashigane/1',
  'norinori': '/juegos/norinori/1',
  'trias':     '/juegos/trias/1',
  'cine':      '/juegos/cine/1',
  'tetonor':   '/juegos/tetonor',
  'fillomino': '/juegos/fillomino/1',
  'estanque':  '/juegos/estanque/1',
  // Agregar más juegos aquí cuando se migren
};

function getGameRoute(gameCode: string): string {
  return GAME_ROUTES[gameCode] || `/juego/${gameCode}`;
}

export default function GamesPage() {
  const { selectedKid } = useKidsStore();
  const { data: games, isLoading, error } = useQuery({
    queryKey: ['games'],
    queryFn: () => gameService.getAllGames('neureka'),
    enabled: !selectedKid,
  });

  // Si hay un perfil kid activo, redirigir a su área
  if (selectedKid) {
    return <Navigate to="/kids/home" replace />;
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 text-neureka-yellow animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-red-400">Error al cargar juegos</p>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
          🎮 Selecciona un juego
        </h1>
        <p className="text-xl text-white/80">
          Elige tu desafío mental favorito
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {games?.map((game: Game) => (
          <Link
            key={game.id}
            to={getGameRoute(game.codigo)}
            className="game-card group"
          >
            <div className="text-center">
              <div className="text-6xl mb-4">{game.icono}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-neureka-purple transition-colors">
                {game.nombre}
              </h3>
              <p className="text-gray-600 mb-4 min-h-[60px]">
                {game.descripcion}
              </p>
              
              <div className="flex justify-center gap-2 pt-4 border-t border-gray-200">
                <span className="px-3 py-1 bg-neureka-purple text-white rounded-full text-sm font-semibold">
                  Jugar ahora
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      
      {games && games.length === 0 && (
        <div className="text-center py-16">
          <p className="text-white/60 text-lg">
            No hay juegos disponibles en este momento
          </p>
        </div>
      )}
    </div>
  );
}
